import {match, Pattern} from 'ts-pattern';

import {isNil, partition} from 'ramda';

import type {PricedItemBatchOperation} from '@dms/api/new-vehicle';

import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import type {PricedItemChange} from '../types/PricedItemChange';

/** The most operations the BE takes in one batch (spec 4.8). */
export const MAX_BATCH_OPERATIONS = 100;

type MatrixPutOperation = Extract<PricedItemBatchOperation, {type: 'variantMatrix.put'}>;

type IndexedOperation = {operation: PricedItemBatchOperation; entryIndex: number};

type BuiltBatch =
  | {
      outcome: 'built';
      /** The rows of each entry that has rows, under the entry title, for the dialog headings. */
      groups: {key: string; title: string; items: PricedItemChange[]}[];
      /** The request operations, in the order of spec 4.2.2. */
      operations: PricedItemBatchOperation[];
      /** The index of the entry of each operation, to name the entry of an `operationIndex`. */
      operationEntryIndexes: number[];
    }
  | {outcome: 'buildFailed'; entryIndex: number; error: unknown};

const isMatrixPut = (operation: PricedItemBatchOperation) => operation.type === 'variantMatrix.put';

/**
 * Each matrix PUT carries the stored cells it read plus its new cells. One PUT with all of them takes
 * the place of the first, because the BE refuses a second matrix write in one batch.
 */
const mergeMatrixPuts = (puts: MatrixPutOperation[]) => {
  const [first] = puts;
  if (isNil(first)) {
    return undefined;
  }
  const cells = new Map(
    puts.flatMap((put) => put.body.cells.map((cell) => [`${cell.trimId}:${cell.engineId}`, cell]))
  );
  return {...first, body: {...first.body, cells: [...cells.values()]}};
};

const mergeIntoFirstPut = (indexed: IndexedOperation[]) => {
  const merged = mergeMatrixPuts(
    indexed.flatMap(({operation}) => (operation.type === 'variantMatrix.put' ? [operation] : []))
  );
  const firstPut = indexed.findIndex(({operation}) => isMatrixPut(operation));
  return indexed.flatMap((item, index) => {
    if (!isMatrixPut(item.operation)) {
      return [item];
    }
    return index === firstPut && !isNil(merged) ? [{...item, operation: merged}] : [];
  });
};

/**
 * The client id of a create, which later operations of the batch can name (F1 addendum 4.4); none
 * for other types and for a create without a client id.
 */
const createdIdsOf = (operation: PricedItemBatchOperation) =>
  'id' in operation && !isNil(operation.id) ? [operation.id] : [];

/**
 * The item ids an operation names in the fields where the BE allows an item created in the batch:
 * the trims of matrix cells, the colour and the trims of a colour trims replace, the trims of a
 * package.
 */
const referencedIdsOf = (operation: PricedItemBatchOperation) =>
  match(operation)
    .with({type: 'variantMatrix.put'}, ({body}) => body.cells.map(({trimId}) => trimId))
    .with({type: 'colourTrims.replace'}, ({targetId, body}) => [
      targetId,
      ...body.trims.map(({trimId}) => trimId),
    ])
    .with({type: Pattern.union('package.create', 'package.update')}, ({body}) =>
      body.trims.map(({trimId}) => trimId)
    )
    .otherwise(() => []);

/**
 * Moves an operation that names an item created later in the batch to directly after the last such
 * create, as the BE refuses a reference before its create (`referenceBeforeCreate`). Keeps the order
 * otherwise.
 */
const createsBeforeReferences = (indexed: IndexedOperation[]) => {
  const createdInBatch = new Set(indexed.flatMap(({operation}) => createdIdsOf(operation)));
  const created = new Set<string>();
  const ordered: IndexedOperation[] = [];
  let waiting: IndexedOperation[] = [];
  const isReady = ({operation}: IndexedOperation) =>
    referencedIdsOf(operation).every((id) => !createdInBatch.has(id) || created.has(id));
  const place = (item: IndexedOperation) => {
    ordered.push(item);
    createdIdsOf(item.operation).forEach((id) => created.add(id));
    const [ready, notReady] = partition(isReady, waiting);
    waiting = notReady;
    ready.forEach(place);
  };
  indexed.forEach((item) => {
    if (isReady(item)) {
      place(item);
    } else {
      waiting.push(item);
    }
  });
  return [...ordered, ...waiting];
};

/**
 * Calls the `build` of every entry and returns what the dialog shows and what the batch request
 * sends: the operations in the order of the entries, the matrix cells in one `variantMatrix.put`,
 * each create before the operations that name its item. A failed `build` names its entry, and
 * nothing is returned to send. A batch sent at once, without a group, uses `buildAtOnce` where an
 * entry has one.
 */
export const buildBatchOperations = async (
  entries: PricedItemBatchEntry[],
  options: {isSentAtOnce?: boolean} = {}
): Promise<BuiltBatch> => {
  const settled = await Promise.allSettled(
    entries.map((entry) =>
      options.isSentAtOnce && !isNil(entry.buildAtOnce) ? entry.buildAtOnce() : entry.build()
    )
  );
  const [failure] = settled.flatMap((result, entryIndex) =>
    result.status === 'rejected' ? [{entryIndex, error: result.reason}] : []
  );
  if (!isNil(failure)) {
    return {outcome: 'buildFailed', ...failure};
  }
  const built = settled.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
  const ordered = createsBeforeReferences(
    mergeIntoFirstPut(
      built.flatMap((entry, entryIndex) =>
        entry.operations.map((operation) => ({operation, entryIndex}))
      )
    )
  );
  return {
    outcome: 'built',
    groups: entries.flatMap((entry, entryIndex) =>
      built[entryIndex].items.length === 0
        ? []
        : [{key: entry.key, title: entry.title, items: built[entryIndex].items}]
    ),
    operations: ordered.map(({operation}) => operation),
    operationEntryIndexes: ordered.map(({entryIndex}) => entryIndex),
  };
};
